import {
  Button,
  DatePicker,
  FormFeedback,
  FormGroup,
  FormLabel,
  FormRow,
  MultiselectDropdown,
  Textarea,
} from '@opositandopn/ui';
import classnames from 'classnames';
import { Form, Formik, FormikProps } from 'formik';
import { IoSearch } from 'react-icons/io5';
import * as Yup from 'yup';

import { BaseComponentProps } from '../../../../types/base-component.types';
import { FormEntity } from '../../../../types/form.types';
import { Visibility } from '../../../questions/api/useMultipleChoiceQuestions';
import { SubjectsForm } from '../../../subjects/components/subjects-form/SubjectsForm';
import { TagsForm } from '../../../tags/components/tags-form/TagsForm';

import styles from './DiagramListFilter.module.scss';

export interface DiagramListFilterForm {
  fromTime: Date;
  toTime: Date;
  sort: string;
  subject: FormEntity;
  unit?: FormEntity;
  lesson?: FormEntity;
  text?: string;
  tags?: FormEntity[];
  visibilities?: Visibility[];
}

type ContainerProps = BaseComponentProps & {
  values: DiagramListFilterForm;
  onSubmit: (values: DiagramListFilterForm) => void;
  onClear: () => void;
};

function DiagramListFilter({
  className,
  values,
  onSubmit,
  onClear,
  testId = 'ui-diagram-list-filter',
}: ContainerProps) {
  return (
    <Formik<DiagramListFilterForm>
      enableReinitialize
      validationSchema={Yup.object().shape({
        subject: Yup.object().required(),
        text: Yup.string().max(
          2000,
          'El texto no debe superar los 2000 caracteres.',
        ),
        tags: Yup.array().of(
          Yup.object().shape({
            id: Yup.string().required(),
            name: Yup.string().required(),
          }),
        ),
      })}
      onSubmit={async (values, { setSubmitting }) => {
        setSubmitting(true);

        onSubmit(values);

        setSubmitting(false);
      }}
      initialValues={{
        fromTime: values.fromTime ?? new Date(),
        toTime: values.toTime ?? new Date(),
        sort: values.sort ?? '-createdAt',
        subject: values.subject ?? {
          id: '',
          name: '',
        },
        unit: values.unit ?? undefined,
        lesson: values.lesson ?? undefined,
        text: values.text ?? undefined,
        tags: values.tags ?? [],
        visibilities: values.visibilities ?? undefined,
      }}
    >
      {({
        values,
        touched,
        errors,
        isSubmitting,
        handleChange,
        handleSubmit,
      }: FormikProps<DiagramListFilterForm>) => (
        <Form
          className={classnames(styles['form'], className)}
          noValidate
          onSubmit={handleSubmit}
          data-testid={testId}
        >
          <FormRow>
            <FormGroup>
              <FormLabel
                info={
                  <>
                    <p>
                      El contenido de este campo puede ser cualquier texto que
                      pueda contener el esquema.
                    </p>
                    <p>
                      Ten en cuenta que estas búsquedas son sensibles a
                      caracteres especiales como las tildes.
                    </p>
                  </>
                }
              >
                Texto libre
              </FormLabel>
              <Textarea
                placeholder="Escribe cualquier texto que pueda contener el esquema..."
                id="text"
                value={values.text ?? ''}
                onChange={handleChange}
                rows={2}
                error={touched.text && !!errors.text}
              />
              {touched.text && errors.text && (
                <FormFeedback variant="danger">{errors.text}</FormFeedback>
              )}
              <FormFeedback>Texto que puede contener el esquema.</FormFeedback>
            </FormGroup>
            <TagsForm />
            <FormGroup>
              <FormLabel>Fecha de creación</FormLabel>
              <DatePicker
                className={styles['date-picker']}
                startDate={values.fromTime}
                endDate={values.toTime}
                onChange={(fromTime, toTime) => {
                  handleChange({
                    target: {
                      name: 'fromTime',
                      value: fromTime,
                    },
                  });

                  handleChange({
                    target: {
                      name: 'toTime',
                      value: toTime,
                    },
                  });
                }}
                size="lg"
                shortcuts={[
                  'today',
                  'yesterday',
                  'lastSevenDays',
                  'monthToDate',
                  'yearToDate',
                ]}
                locale="es"
              />
              <FormFeedback>Fecha en la que se creó el esquema.</FormFeedback>
            </FormGroup>
          </FormRow>
          <SubjectsForm />

          <FormRow>
            <FormGroup>
              <FormLabel
                info={
                  <>
                    <p>
                      La visibilidad del esquema se refiere a quién puede ver el
                      esquema.
                    </p>
                    <p>Tipos:</p>
                    <ul>
                      <li>
                        La visibilidad <strong>pública</strong> indica que el
                        esquema es visible para todos los usuarios, tanto los
                        Premium como los gratuitos.
                      </li>
                      <li>
                        La visibilidad <strong>privada</strong> indica que el
                        esquema es visible solo para los usuarios Premium.
                      </li>
                    </ul>
                  </>
                }
              >
                Visibilidad del esquema
              </FormLabel>
              <MultiselectDropdown
                placeholder="Selecciona uno o varios..."
                onSelect={(values: (string | number)[]) => {
                  handleChange({
                    target: {
                      name: 'visibilities',
                      value: values as string[],
                    },
                  });
                }}
                options={[
                  {
                    value: 'public',
                    label: 'Pública',
                    selected: values.visibilities?.includes('public'),
                  },
                  {
                    value: 'private',
                    label: 'Privada',
                    selected: values.visibilities?.includes('private'),
                  },
                ]}
              />
              <FormFeedback></FormFeedback>
            </FormGroup>
          </FormRow>

          <div className={styles['footer']}>
            <Button
              className={styles['button']}
              variant="outline-primary"
              onClick={onClear}
            >
              Limpiar
            </Button>
            <Button
              className={styles['button']}
              loading={isSubmitting}
              disabled={isSubmitting}
              variant="primary"
              type="submit"
              suffixIcon={<IoSearch />}
            >
              Buscar
            </Button>
          </div>
        </Form>
      )}
    </Formik>
  );
}

export { DiagramListFilter };
